import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Download, Share2, Smartphone, CheckCircle2, AlertCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const Install = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkStandalone = window.matchMedia("(display-mode: standalone)").matches;
    setIsStandalone(checkStandalone);
    setIsInstalled(checkStandalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Listen for successful install
    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const features = [
    { title: "Works Offline", description: "Transform testimonials even without internet" },
    { title: "Native Feel", description: "App-like experience with smooth animations" },
    { title: "Push Notifications", description: "Get notified when transformations complete" },
    { title: "Instant Loading", description: "Lightning fast startup from your home screen" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-background to-accent/20">
      <Navbar />

      <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-brand-600 to-brand-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-brand-500/25">
            <Smartphone className="h-10 w-10 text-white" />
          </div>
          <h1 className="font-editorial text-3xl md:text-4xl text-foreground mb-2">
            Install Praise
          </h1>
          <p className="text-lg text-muted-foreground">
            Get the full app experience on your device
          </p>
        </div>

        {isInstalled || isStandalone ? (
          <Card className="border-success/50 bg-success/5">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                App Installed!
              </h2>
              <p className="text-muted-foreground">
                You're using Praise as an installed app. Enjoy the full experience!
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {deferredPrompt ? (
              <Card className="border-brand-200 shadow-lg">
                <CardContent className="pt-6 text-center">
                  <Button
                    size="lg"
                    onClick={handleInstall}
                    className="bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white gap-2"
                  >
                    <Download className="h-5 w-5" />
                    Install Praise App
                  </Button>
                  <p className="text-sm text-muted-foreground mt-4">
                    Add to your home screen for instant access
                  </p>
                </CardContent>
              </Card>
            ) : isIOS ? (
              <Card className="border-brand-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-brand-600" />
                    Install on iOS
                  </CardTitle>
                  <CardDescription>
                    Follow these steps to add Praise to your home screen
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="mt-0.5">1</Badge>
                    <div>
                      <p className="font-medium">Tap the Share button</p>
                      <p className="text-sm text-muted-foreground">
                        At the bottom of Safari (the square with arrow pointing up)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="mt-0.5">2</Badge>
                    <div>
                      <p className="font-medium">Scroll down and tap "Add to Home Screen"</p>
                      <p className="text-sm text-muted-foreground">
                        You may need to scroll down in the share menu
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="mt-0.5">3</Badge>
                    <div>
                      <p className="font-medium">Tap "Add" in the top right</p>
                      <p className="text-sm text-muted-foreground">
                        The Praise app icon will appear on your home screen
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border">
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    Install via Browser Menu
                  </h2>
                  <p className="text-muted-foreground">
                    Open your browser menu (⋮) and select "Install app" or "Add to Home Screen"
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Features */}
        <div className="mt-12">
          <h2 className="font-editorial text-2xl text-center text-foreground mb-6">
            Why Install?
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <Card key={index} className="border-border">
                <CardContent className="pt-4">
                  <h3 className="font-medium text-foreground mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Install;
